/**
 * Incident Detection Utilities
 *
 * Detects service outages from error patterns and generates
 * signatures for deduplication.
 */

export type AffectedService =
  | 'authentication'
  | 'database'
  | 'api'
  | 'hosting'
  | 'storage'
  | 'realtime'
  | 'external'
  | 'application'
  | 'unknown';

export type IncidentStatus = 'investigating' | 'identified' | 'monitoring' | 'resolved';

export interface DetectedIncident {
  signature: string;
  service: AffectedService;
  isLikelyOutage: boolean;
  searchQuery: string; // For AI to search
  statusPageUrl?: string;
}

interface ErrorPattern {
  pattern: RegExp;
  signature: string;
  service: AffectedService;
  searchQuery: string;
  statusPageUrl?: string;
}

/**
 * Known error patterns that indicate service outages
 */
const ERROR_PATTERNS: ErrorPattern[] = [
  // Supabase Auth
  {
    pattern:
      /CORS.*supabase.*auth|supabase\.co\/auth.*blocked|Access-Control-Allow-Origin.*supabase/i,
    signature: 'CORS_SUPABASE_AUTH',
    service: 'authentication',
    searchQuery: 'Supabase authentication outage status',
    statusPageUrl: 'https://status.supabase.com',
  },
  // Supabase Database
  {
    pattern: /supabase.*connection.*refused|supabase.*ECONNREFUSED|supabase\.co.*503/i,
    signature: 'CONNECTION_SUPABASE_DB',
    service: 'database',
    searchQuery: 'Supabase database connectivity issues',
    statusPageUrl: 'https://status.supabase.com',
  },
  // Supabase API Gateway
  {
    pattern: /supabase.*502|supabase.*504|supabase.*gateway/i,
    signature: 'GATEWAY_SUPABASE',
    service: 'api',
    searchQuery: 'Supabase API gateway outage',
    statusPageUrl: 'https://status.supabase.com',
  },
  // Vercel
  {
    pattern: /vercel.*503|vercel.*502|FUNCTION_INVOCATION_FAILED/i,
    signature: 'VERCEL_FUNCTIONS',
    service: 'hosting',
    searchQuery: 'Vercel serverless functions outage',
    statusPageUrl: 'https://www.vercel-status.com',
  },
  // Own-code deploy faults: ESM require errors, React render/hydration failures
  {
    pattern:
      /Failed to load external module|ERR_REQUIRE_ESM|An error occurred in the Server Components render|Hydration failed because the initial UI does not match|server could not finish this Suspense boundary|Minified React error #4(?:1[6-9]|2[0-5])\b/i,
    signature: 'APP_DEPLOY_FAULT',
    service: 'application',
    searchQuery: 'Next.js server-side render failure deploy',
  },
  // Control-flow leak: a server action called redirect() to the user's current route,
  // or a try/catch swallowed the redirect digest and surfaced the raw string to the client.
  // NEXT_REDIRECT reaching the client is always actionable, never benign.
  {
    pattern: /\bNEXT_REDIRECT\b/,
    signature: 'APP_CONTROL_FLOW_LEAK',
    service: 'application',
    searchQuery: 'NEXT_REDIRECT leaked to client UI server action redirect target self',
  },
  // Supabase Auth runtime failures — PKCE verifier loss, OAuth denial, expired/used links,
  // missing code params, malformed callbacks. Always actionable: a real user is locked out.
  {
    pattern:
      /AuthApiError|code verifier should be non-empty|both auth code and code verifier|Email link is invalid or has expired|Missing code parameter|access_denied/i,
    signature: 'AUTH_PKCE_OR_API_FAILURE',
    service: 'authentication',
    searchQuery: 'Supabase PKCE code verifier missing signup callback AuthApiError',
    statusPageUrl: 'https://status.supabase.com',
  },
  // Generic network failures
  {
    pattern: /Failed to fetch|NetworkError|net::ERR_FAILED|ERR_CONNECTION_REFUSED/i,
    signature: 'NETWORK_GENERIC',
    service: 'unknown',
    searchQuery: 'cloud service outages today',
  },
  // Supabase Storage
  {
    pattern: /supabase.*storage.*error|storage\.supabase/i,
    signature: 'SUPABASE_STORAGE',
    service: 'storage',
    searchQuery: 'Supabase storage issues',
    statusPageUrl: 'https://status.supabase.com',
  },
  // Supabase Realtime
  {
    pattern: /realtime.*connection|websocket.*supabase/i,
    signature: 'SUPABASE_REALTIME',
    service: 'realtime',
    searchQuery: 'Supabase realtime websocket issues',
    statusPageUrl: 'https://status.supabase.com',
  },
];

/**
 * Analyzes an error and determines if it's likely a service outage
 */
export function detectIncident(error: unknown): DetectedIncident | null {
  const errorString = normalizeError(error);

  for (const pattern of ERROR_PATTERNS) {
    if (pattern.pattern.test(errorString)) {
      return {
        signature: pattern.signature,
        service: pattern.service,
        isLikelyOutage: true,
        searchQuery: pattern.searchQuery,
        statusPageUrl: pattern.statusPageUrl,
      };
    }
  }

  // Check for generic outage indicators
  if (isLikelyOutage(errorString)) {
    return {
      signature: 'UNKNOWN_OUTAGE',
      service: 'unknown',
      isLikelyOutage: true,
      searchQuery: 'service outage today',
    };
  }

  return null;
}

/**
 * Normalizes various error types to a searchable string
 */
function normalizeError(error: unknown): string {
  if (error instanceof Error) {
    return `${error.name}: ${error.message} ${error.stack || ''}`;
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error && typeof error === 'object') {
    return JSON.stringify(error);
  }
  return String(error);
}

/**
 * Heuristics to determine if an error is likely a service outage
 * rather than a user/code issue
 */
function isLikelyOutage(errorString: string): boolean {
  const outageIndicators = [
    /503/,
    /502/,
    /504/,
    /service unavailable/i,
    /bad gateway/i,
    /gateway timeout/i,
    /connection refused/i,
    /ECONNREFUSED/,
    /ETIMEDOUT/,
    /Failed to fetch/,
    /network error/i,
    // App-deploy fault markers (own-code regression, not infra outage)
    /Failed to load external module/i,
    /ERR_REQUIRE_ESM/,
    /An error occurred in the Server Components render/i,
    /Hydration failed because the initial UI does not match/i,
    /server could not finish this Suspense boundary/i,
    /Minified React error #4(?:1[6-9]|2[0-5])\b/,
    // Control-flow leak — server redirect() digest surfaced to the client
    /\bNEXT_REDIRECT\b/,
    // Auth PKCE / OAuth failures — real user locked out
    /AuthApiError|code verifier should be non-empty|both auth code and code verifier|Email link is invalid or has expired|Missing code parameter|access_denied/i,
  ];

  // Must match at least one outage indicator
  const matchesOutagePattern = outageIndicators.some((p) => p.test(errorString));

  // Should NOT match user error patterns
  const userErrorPatterns = [
    /401/,
    /403/,
    /404/,
    /validation/i,
    /invalid.*input/i,
    /required/i,
    /unauthorized/i,
  ];
  const isUserError = userErrorPatterns.some((p) => p.test(errorString));

  return matchesOutagePattern && !isUserError;
}

/**
 * Gets user-friendly service name
 */
export function getServiceDisplayName(service: AffectedService): string {
  const names: Record<AffectedService, string> = {
    authentication: 'Sign In',
    database: 'Data Services',
    api: 'API',
    hosting: 'Application',
    storage: 'File Storage',
    realtime: 'Live Updates',
    external: 'External Service',
    application: 'Application',
    unknown: 'Services',
  };
  return names[service];
}

/**
 * Gets status color for UI
 */
export function getStatusColor(status: IncidentStatus): string {
  const colors: Record<IncidentStatus, string> = {
    investigating: 'text-amber-600 dark:text-amber-400',
    identified: 'text-orange-600 dark:text-orange-400',
    monitoring: 'text-blue-600 dark:text-blue-400',
    resolved: 'text-green-600 dark:text-green-400',
  };
  return colors[status];
}

/**
 * Liveness window for the public status banner.
 *
 * Incidents are only auto-resolved by the `/api/system/health-check` cron, which
 * runs exclusively against PROD and bumps `last_checked_at` every ~5 minutes for
 * each still-active incident (so `last_checked_at` is a resolver heartbeat). Any
 * environment where that cron does NOT run (STAGING / local-dev) never resolves its
 * incidents, so a single transient error would otherwise show in the banner forever.
 *
 * The public GET therefore only surfaces incidents whose heartbeat is within this
 * window — comfortably above the 5-min cron cadence (tolerates ~24 missed runs),
 * yet short enough that stale/orphaned incidents stop showing on their own. This
 * also fail-safes PROD: if the resolver cron ever silently dies, customers stop
 * seeing a frozen weeks-old "investigating" banner instead of being misled by it.
 */
export const ACTIVE_INCIDENT_WINDOW_MS = 2 * 60 * 60 * 1000; // 2 hours

/**
 * Returns the ISO cutoff before which an incident is considered stale/orphaned and
 * is excluded from the public status banner. Incidents with `last_checked_at` older
 * than this have not been heartbeated by the resolver and should not be shown.
 *
 * @param now - epoch ms to measure from (injectable for testing); defaults to Date.now()
 */
export function getActiveIncidentCutoff(now: number = Date.now()): string {
  return new Date(now - ACTIVE_INCIDENT_WINDOW_MS).toISOString();
}
